import { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError } from "axios";
import i18next from "i18next";
import { enqueueSnackbar } from "notistack";
import call from "./call";
import { documentCraftingHeaders } from "../Helpers/documentCraftingWorkspace";
import {
  DocumentCraftingAnswersPayload,
  DocumentCraftingApiError,
  DocumentCraftingCapabilities,
  DocumentCraftingCreatePayload,
  DocumentCraftingCreateResponse,
  DocumentCraftingDocumentDetail,
  DocumentCraftingDocumentLabels,
  DocumentCraftingDocumentLabelsUpdate,
  DocumentCraftingDocumentListParams,
  DocumentCraftingDocumentListResponse,
  DocumentCraftingDocumentUpdate,
  DocumentCraftingDuplicatePrefill,
  DocumentCraftingFileKindPath,
  DocumentCraftingFiles,
  DocumentCraftingLabel,
  DocumentCraftingLabelCreate,
  DocumentCraftingLabelUpdate,
  DocumentCraftingLabelsResponse,
  DocumentCraftingLanguage,
  DocumentCraftingMembersResponse,
  DocumentCraftingModelsResponse,
  DocumentCraftingMySettings,
  DocumentCraftingRefinePayload,
  DocumentCraftingRevisePayload,
  DocumentCraftingRevisionDetail,
  DocumentCraftingRevisionSummary,
  DocumentCraftingRun,
  DocumentCraftingShares,
  DocumentCraftingSharesUpdate,
  DocumentCraftingStartedRevision,
  DocumentCraftingTranslationResponse,
  DocumentCraftingTypeKey,
  DocumentCraftingTypesResponse,
  DocumentCraftingUploadRevisionPayload,
  DocumentCraftingUsage,
  DocumentCraftingWorkspaceSettings,
  DocumentCraftingWorkspaceSettingsUpdate,
} from "../Models/documentCrafting";

const BASE = "/v1/document-crafting";

type Snack = NonNullable<AxiosRequestConfig["snack"]>;

/** The error part of a request's snack config; announced by this module's
 * interceptor (below) so the API's error `code` can pick the message. */
interface DocumentCraftingErrorSnack {
  error?: string;
  /** Status → key; null keeps that status silent. */
  statuses: Record<number, string | null>;
  /** Error codes the caller handles itself (no snack). */
  quietCodes: string[];
}

declare module "axios" {
  export interface AxiosRequestConfig {
    documentCraftingErrors?: DocumentCraftingErrorSnack;
  }
}

// Every call carries the admin workspace header (empty for everyone else).
// The success part of `snack` goes to the shared interceptor in `call.ts`;
// errors are announced by `announceError` below.
const options = (
  snack?: Snack,
  extra: AxiosRequestConfig = {},
  quietCodes: string[] = [],
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    ...extra,
    headers: documentCraftingHeaders(),
  };
  if (!snack) return config;
  const { success, error, ...statuses } = snack;
  const announced: Snack = success ? { success } : {};
  const errors: DocumentCraftingErrorSnack = { error, statuses: {}, quietCodes };
  Object.entries(statuses).forEach(([key, value]) => {
    const status = Number(key);
    if (status < 400) announced[status] = value;
    else errors.statuses[status] = value;
  });
  return { ...config, snack: announced, documentCraftingErrors: errors };
};

// Snacks shared by every request that starts a run.
const RUN_START_SNACK = {
  403: "documentCrafting.startForbidden",
  413: "documentCrafting.fileTooLarge",
  415: "documentCrafting.wrongFileType",
  429: "documentCrafting.tooManyRuns",
};

/** The `{message, code, ...}` body of a failed request, if it has one. */
export const getDocumentCraftingError = (
  error: unknown,
): DocumentCraftingApiError | null => {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data;
  return data && typeof data === "object" && !(data instanceof Blob)
    ? (data as DocumentCraftingApiError)
    : null;
};

/** The readable text of a failed request's error `code`
 * (`snack:documentCrafting.errors.<code>`, filled with the body's extras such
 * as `filename` or `max_mb`); null when the code has no text. */
export const documentCraftingErrorText = (error: unknown): string | null => {
  const body = getDocumentCraftingError(error);
  if (!body?.code) return null;
  const key = `documentCrafting.errors.${body.code}`;
  if (!i18next.exists(key, { ns: "snack" })) return null;
  return i18next.t(key, { ns: "snack", replace: body });
};

/** `documentCraftingErrorText`, else the server's message, else `fallback`. */
export const documentCraftingErrorMessage = (
  error: unknown,
  fallback: string,
): string =>
  documentCraftingErrorText(error) ??
  getDocumentCraftingError(error)?.message ??
  fallback;

// Error snacks of this module: the text of the error code when there is one,
// else the request's key for the status, else its generic error key.
const announceError = (error: AxiosError) => {
  const errors = error.config?.documentCraftingErrors;
  const status = error.response?.status;
  if (!errors || !status) return;
  const code = getDocumentCraftingError(error)?.code;
  if (code && errors.quietCodes.includes(code)) return;
  const statusKey = errors.statuses[status];
  if (statusKey === null) return;
  const fallbackKey = statusKey ?? errors.error;
  const message =
    documentCraftingErrorText(error) ??
    (fallbackKey ? i18next.t(fallbackKey, { ns: "snack" }) : null);
  if (!message) return;
  enqueueSnackbar(message, {
    variant: "error",
    key: `snackbar-error-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
  });
};

call.interceptors.response.use(null, (error: unknown) => {
  if (isAxiosError(error)) announceError(error);
  return Promise.reject(error);
});

/** The JSON `payload` part plus the new files as `files[<field_key>]`. */
const multipart = (payload: object, files?: DocumentCraftingFiles) => {
  const form = new FormData();
  form.append("payload", JSON.stringify(payload));
  if (files) {
    Object.keys(files).forEach((fieldKey) =>
      files[fieldKey].forEach((file) =>
        form.append(`files[${fieldKey}]`, file),
      ),
    );
  }
  return form;
};

// Drops empty filters so the URL only carries what is set.
const listParams = (params: DocumentCraftingDocumentListParams) => {
  const clean: Record<string, unknown> = {};
  (Object.keys(params) as (keyof DocumentCraftingDocumentListParams)[]).forEach(
    (key) => {
      const value = params[key];
      if (value === undefined || value === null || value === "") return;
      if (value === false) return;
      if (Array.isArray(value) && !value.length) return;
      clean[key] = value;
    },
  );
  return clean;
};

// URLs of files (relative to the API base, for `call`, never for `<img src>`)

const documentPath = (documentId: number) =>
  `${BASE}/documents/${documentId}`;
const revisionPath = (documentId: number, revisionId: number) =>
  `${documentPath(documentId)}/revisions/${revisionId}`;

export const documentCraftingFileUrls = {
  typeThumbnail: (key: DocumentCraftingTypeKey) =>
    `${BASE}/types/${key}/thumbnail/`,
  revisionFile: (
    documentId: number,
    revisionId: number,
    kind: DocumentCraftingFileKindPath,
  ) => `${revisionPath(documentId, revisionId)}/files/${kind}/`,
  /** `page` is 1-based. */
  revisionPage: (documentId: number, revisionId: number, page: number) =>
    `${revisionPath(documentId, revisionId)}/files/page/${page}/`,
  upload: (documentId: number, uploadId: number) =>
    `${documentPath(documentId)}/uploads/${uploadId}/file/`,
  translationPdf: (
    documentId: number,
    revisionId: number,
    language: DocumentCraftingLanguage,
  ) =>
    `${revisionPath(documentId, revisionId)}/translations/${language}/files/pdf/`,
  translationPage: (
    documentId: number,
    revisionId: number,
    language: DocumentCraftingLanguage,
    page: number,
  ) =>
    `${revisionPath(documentId, revisionId)}/translations/${language}/files/page/${page}/`,
};

// Module

export const getDocumentCraftingCapabilities =
  async (): Promise<DocumentCraftingCapabilities> => {
    const response = await call.get<DocumentCraftingCapabilities>(
      `${BASE}/capabilities/`,
      options({ error: "documentCrafting.capabilitiesFetchError" }),
    );
    return response.data;
  };

export const getDocumentCraftingTypes =
  async (): Promise<DocumentCraftingTypesResponse> => {
    const response = await call.get<DocumentCraftingTypesResponse>(
      `${BASE}/types/`,
      options({ error: "documentCrafting.typesFetchError" }),
    );
    return response.data;
  };

export const getDocumentCraftingModels =
  async (): Promise<DocumentCraftingModelsResponse> => {
    const response = await call.get<DocumentCraftingModelsResponse>(
      `${BASE}/models/`,
      options({ error: "documentCrafting.modelsFetchError" }),
    );
    return response.data;
  };

export const getDocumentCraftingMembers = async (
  q: string,
): Promise<DocumentCraftingMembersResponse> => {
  const response = await call.get<DocumentCraftingMembersResponse>(
    `${BASE}/members/`,
    options(
      { error: "documentCrafting.membersFetchError" },
      { params: q ? { q } : {} },
    ),
  );
  return response.data;
};

// Documents

export const getDocumentCraftingDocuments = async (
  params: DocumentCraftingDocumentListParams,
): Promise<DocumentCraftingDocumentListResponse> => {
  const response = await call.get<DocumentCraftingDocumentListResponse>(
    `${BASE}/documents/`,
    options(
      { error: "documentCrafting.documentsFetchError" },
      { params: listParams(params) },
    ),
  );
  return response.data;
};

export const createDocumentCraftingDocument = async (
  payload: DocumentCraftingCreatePayload,
  files?: DocumentCraftingFiles,
): Promise<DocumentCraftingCreateResponse> => {
  const response = await call.post<DocumentCraftingCreateResponse>(
    `${BASE}/documents/`,
    multipart(payload, files),
    options({
      error: "documentCrafting.documentCreateError",
      400: "documentCrafting.invalidForm",
      ...RUN_START_SNACK,
    }),
  );
  return response.data;
};

export const getDocumentCraftingDocument = async (
  documentId: number,
): Promise<DocumentCraftingDocumentDetail> => {
  const response = await call.get<DocumentCraftingDocumentDetail>(
    `${documentPath(documentId)}/`,
    options({
      error: "documentCrafting.documentFetchError",
      403: "documentCrafting.accessDenied",
      404: "documentCrafting.documentNotFound",
    }),
  );
  return response.data;
};

export const updateDocumentCraftingDocument = async (
  documentId: number,
  data: DocumentCraftingDocumentUpdate,
): Promise<DocumentCraftingDocumentDetail> => {
  const response = await call.patch<DocumentCraftingDocumentDetail>(
    `${documentPath(documentId)}/`,
    data,
    options({
      success: "documentCrafting.documentUpdateSuccess",
      error: "documentCrafting.documentUpdateError",
      403: "documentCrafting.accessDenied",
    }),
  );
  return response.data;
};

export const deleteDocumentCraftingDocument = async (
  documentId: number,
): Promise<void> => {
  await call.delete(
    `${documentPath(documentId)}/`,
    options({
      success: "documentCrafting.documentDeleteSuccess",
      error: "documentCrafting.documentDeleteError",
      403: "documentCrafting.accessDenied",
    }),
  );
};

export const getDocumentCraftingShares = async (
  documentId: number,
): Promise<DocumentCraftingShares> => {
  const response = await call.get<DocumentCraftingShares>(
    `${documentPath(documentId)}/shares/`,
    options({ error: "documentCrafting.sharesFetchError" }),
  );
  return response.data;
};

export const updateDocumentCraftingShares = async (
  documentId: number,
  data: DocumentCraftingSharesUpdate,
): Promise<DocumentCraftingShares> => {
  const response = await call.put<DocumentCraftingShares>(
    `${documentPath(documentId)}/shares/`,
    data,
    options({
      success: "documentCrafting.sharesSaveSuccess",
      error: "documentCrafting.sharesSaveError",
      403: "documentCrafting.accessDenied",
    }),
  );
  return response.data;
};

export const setDocumentCraftingDocumentLabels = async (
  documentId: number,
  data: DocumentCraftingDocumentLabelsUpdate,
): Promise<DocumentCraftingDocumentLabels> => {
  const response = await call.put<DocumentCraftingDocumentLabels>(
    `${documentPath(documentId)}/labels/`,
    data,
    options({
      error: "documentCrafting.documentLabelsSaveError",
      403: "documentCrafting.accessDenied",
      404: "documentCrafting.labelNotFound",
    }),
  );
  return response.data;
};

/** Returns the prefilled create form; the new document is then created with
 * `createDocumentCraftingDocument`. */
export const duplicateDocumentCraftingRevision = async (
  documentId: number,
  revisionId: number,
): Promise<DocumentCraftingDuplicatePrefill> => {
  const response = await call.post<DocumentCraftingDuplicatePrefill>(
    `${revisionPath(documentId, revisionId)}/duplicate/`,
    {},
    options({ error: "documentCrafting.duplicateError" }),
  );
  return response.data;
};

// Labels

export const getDocumentCraftingLabels = async (
  includeArchived: boolean,
): Promise<DocumentCraftingLabelsResponse> => {
  const response = await call.get<DocumentCraftingLabelsResponse>(
    `${BASE}/labels/`,
    options(
      { error: "documentCrafting.labelsFetchError" },
      { params: includeArchived ? { include_archived: true } : {} },
    ),
  );
  return response.data;
};

export const createDocumentCraftingLabel = async (
  data: DocumentCraftingLabelCreate,
): Promise<DocumentCraftingLabel> => {
  const response = await call.post<DocumentCraftingLabel>(
    `${BASE}/labels/`,
    data,
    options({
      success: "documentCrafting.labelCreateSuccess",
      error: "documentCrafting.labelCreateError",
      403: "documentCrafting.accessDenied",
      409: "documentCrafting.labelExists",
    }),
  );
  return response.data;
};

export const updateDocumentCraftingLabel = async (
  labelId: number,
  data: DocumentCraftingLabelUpdate,
): Promise<DocumentCraftingLabel> => {
  const response = await call.patch<DocumentCraftingLabel>(
    `${BASE}/labels/${labelId}/`,
    data,
    options({
      success: "documentCrafting.labelUpdateSuccess",
      error: "documentCrafting.labelUpdateError",
      403: "documentCrafting.accessDenied",
      404: "documentCrafting.labelNotFound",
      409: "documentCrafting.labelExists",
    }),
  );
  return response.data;
};

export const deleteDocumentCraftingLabel = async (
  labelId: number,
): Promise<{ deleted_assignments: number }> => {
  const response = await call.delete<{ deleted_assignments: number }>(
    `${BASE}/labels/${labelId}/`,
    options({
      success: "documentCrafting.labelDeleteSuccess",
      error: "documentCrafting.labelDeleteError",
      403: "documentCrafting.accessDenied",
      404: "documentCrafting.labelNotFound",
    }),
  );
  return response.data;
};

// Settings

export const getDocumentCraftingMySettings =
  async (): Promise<DocumentCraftingMySettings> => {
    const response = await call.get<DocumentCraftingMySettings>(
      `${BASE}/settings/me/`,
      options({ error: "documentCrafting.settingsFetchError" }),
    );
    return response.data;
  };

/** `notify` = false saves silently, e.g. remembered list view settings.
 * With `notify`, 400 `invalid_request` is not announced: the form marks the
 * rejected `field`. */
export const updateDocumentCraftingMySettings = async (
  data: Partial<DocumentCraftingMySettings>,
  notify = true,
): Promise<DocumentCraftingMySettings> => {
  const response = await call.put<DocumentCraftingMySettings>(
    `${BASE}/settings/me/`,
    data,
    options(
      {
        success: notify ? "documentCrafting.settingsSaveSuccess" : undefined,
        error: "documentCrafting.settingsSaveError",
      },
      {},
      notify ? ["invalid_request"] : [],
    ),
  );
  return response.data;
};

export const getDocumentCraftingWorkspaceSettings =
  async (): Promise<DocumentCraftingWorkspaceSettings> => {
    const response = await call.get<DocumentCraftingWorkspaceSettings>(
      `${BASE}/settings/workspace/`,
      options({ error: "documentCrafting.workspaceSettingsFetchError" }),
    );
    return response.data;
  };

/** 400 `invalid_request` and `model_not_accessible` are not announced: the
 * form marks the rejected `field` (other 400 codes are announced). */
export const updateDocumentCraftingWorkspaceSettings = async (
  data: DocumentCraftingWorkspaceSettingsUpdate,
): Promise<DocumentCraftingWorkspaceSettings> => {
  const response = await call.put<DocumentCraftingWorkspaceSettings>(
    `${BASE}/settings/workspace/`,
    data,
    options(
      {
        success: "documentCrafting.workspaceSettingsSaveSuccess",
        error: "documentCrafting.workspaceSettingsSaveError",
        400: "documentCrafting.invalidSettings",
        403: "documentCrafting.accessDenied",
      },
      {},
      ["invalid_request", "model_not_accessible"],
    ),
  );
  return response.data;
};

// Revisions and drafts

export const getDocumentCraftingRevision = async (
  documentId: number,
  revisionId: number,
): Promise<DocumentCraftingRevisionDetail> => {
  const response = await call.get<DocumentCraftingRevisionDetail>(
    `${revisionPath(documentId, revisionId)}/`,
    options({
      error: "documentCrafting.revisionFetchError",
      403: "documentCrafting.accessDenied",
      404: "documentCrafting.revisionNotFound",
    }),
  );
  return response.data;
};

/** For `downloadDocumentBlob(response, "<title>_r<N>.<ext>")`. */
export const downloadDocumentCraftingRevisionFile = (
  documentId: number,
  revisionId: number,
  kind: DocumentCraftingFileKindPath,
): Promise<AxiosResponse<Blob>> =>
  call.get<Blob>(
    documentCraftingFileUrls.revisionFile(documentId, revisionId, kind),
    options(
      { error: "documentCrafting.downloadError" },
      { params: { download: 1 }, responseType: "blob" },
    ),
  );

export const downloadDocumentCraftingTranslationPdf = (
  documentId: number,
  revisionId: number,
  language: DocumentCraftingLanguage,
): Promise<AxiosResponse<Blob>> =>
  call.get<Blob>(
    documentCraftingFileUrls.translationPdf(documentId, revisionId, language),
    options(
      { error: "documentCrafting.downloadError" },
      { params: { download: 1 }, responseType: "blob" },
    ),
  );

/** Any file of the module as a Blob (images, PDF previews); silent on
 * failure, the caller shows it inline. */
export const getDocumentCraftingBlob = async (url: string): Promise<Blob> => {
  const response = await call.get<Blob>(url, options(undefined, {
    responseType: "blob",
  }));
  return response.data;
};

/** 409 `draft_exists` is not announced (other 409s are): the caller offers to continue or
 * discard the draft (`getDocumentCraftingError(error).draft_revision_id`). */
export const createDocumentCraftingRevision = async (
  documentId: number,
  payload: DocumentCraftingRevisePayload,
  files?: DocumentCraftingFiles,
): Promise<DocumentCraftingStartedRevision> => {
  const response = await call.post<DocumentCraftingStartedRevision>(
    `${documentPath(documentId)}/revisions/`,
    multipart(payload, files),
    options(
      {
        error: "documentCrafting.revisionCreateError",
        400: "documentCrafting.invalidForm",
        ...RUN_START_SNACK,
      },
      {},
      ["draft_exists"],
    ),
  );
  return response.data;
};

/** 409 `draft_exists` is not announced (other 409s are): the caller offers to
 * open the draft or to discard it and upload (`discard_existing_draft`). */
export const uploadDocumentCraftingRevision = async (
  documentId: number,
  payload: DocumentCraftingUploadRevisionPayload,
): Promise<DocumentCraftingStartedRevision> => {
  const form = new FormData();
  form.append("file", payload.file);
  form.append("base_revision_id", String(payload.base_revision_id));
  form.append("note", payload.note ?? "");
  if (payload.discard_existing_draft) form.append("discard_existing_draft", "true");
  const response = await call.post<DocumentCraftingStartedRevision>(
    `${documentPath(documentId)}/revisions/upload/`,
    form,
    options(
      {
        error: "documentCrafting.revisionUploadError",
        ...RUN_START_SNACK,
      },
      {},
      ["draft_exists"],
    ),
  );
  return response.data;
};

/** 409 `save_conflict` is not announced (other 409s are): the caller asks whether to save
 * anyway (`force`). */
export const saveDocumentCraftingDraft = async (
  documentId: number,
  revisionId: number,
  force = false,
): Promise<DocumentCraftingRevisionSummary> => {
  const response = await call.post<DocumentCraftingRevisionSummary>(
    `${documentPath(documentId)}/drafts/${revisionId}/save/`,
    { force },
    options(
      {
        success: "documentCrafting.draftSaveSuccess",
        error: "documentCrafting.draftSaveError",
      },
      {},
      ["save_conflict"],
    ),
  );
  return response.data;
};

export const refineDocumentCraftingDraft = async (
  documentId: number,
  revisionId: number,
  payload: DocumentCraftingRefinePayload,
): Promise<{ run_id: number }> => {
  const response = await call.post<{ run_id: number }>(
    `${documentPath(documentId)}/drafts/${revisionId}/refine/`,
    payload,
    options({
      error: "documentCrafting.draftRefineError",
      ...RUN_START_SNACK,
    }),
  );
  return response.data;
};

/** A saved diagram revision into an A4 document: starts a revise run of the
 * target (its draft gets the diagram as a picture block). */
export const insertDocumentCraftingDiagram = async (
  documentId: number,
  revisionId: number,
  payload: { target_document_id: number; discard_existing_draft?: boolean },
): Promise<{ document_id: number; revision_id: number; run_id: number }> => {
  const response = await call.post<{ document_id: number; revision_id: number; run_id: number }>(
    `${revisionPath(documentId, revisionId)}/insert-into/`,
    payload,
    options({
      error: "documentCrafting.diagramInsertError",
      ...RUN_START_SNACK,
    }),
  );
  return response.data;
};

export const discardDocumentCraftingDraft = async (
  documentId: number,
  revisionId: number,
): Promise<void> => {
  await call.delete(
    `${documentPath(documentId)}/drafts/${revisionId}/`,
    options({
      success: "documentCrafting.draftDiscardSuccess",
      error: "documentCrafting.draftDiscardError",
    }),
  );
};

export const requestDocumentCraftingTranslation = async (
  documentId: number,
  revisionId: number,
  language: DocumentCraftingLanguage,
): Promise<DocumentCraftingTranslationResponse> => {
  const response = await call.post<DocumentCraftingTranslationResponse>(
    `${revisionPath(documentId, revisionId)}/translations/`,
    { language },
    options({
      // 202: a new preview is being made; 200: the cached one is returned.
      202: "documentCrafting.translationRequestSuccess",
      200: null,
      error: "documentCrafting.translationRequestError",
      403: "documentCrafting.startForbidden",
      429: "documentCrafting.tooManyRuns",
    }),
  );
  return response.data;
};

export const getDocumentCraftingUsage = async (
  documentId: number,
): Promise<DocumentCraftingUsage> => {
  const response = await call.get<DocumentCraftingUsage>(
    `${documentPath(documentId)}/usage/`,
    options({ error: "documentCrafting.usageFetchError" }),
  );
  return response.data;
};

// Runs

/** Polled every 2 s, so it shows no snack; the run page shows errors. */
export const getDocumentCraftingRun = async (
  runId: number,
): Promise<DocumentCraftingRun> => {
  const response = await call.get<DocumentCraftingRun>(
    `${BASE}/runs/${runId}/`,
    options(),
  );
  return response.data;
};

export const answerDocumentCraftingRun = async (
  runId: number,
  payload: DocumentCraftingAnswersPayload,
): Promise<DocumentCraftingRun> => {
  const response = await call.post<DocumentCraftingRun>(
    `${BASE}/runs/${runId}/answers/`,
    payload,
    options(
      {
        error: "documentCrafting.answersSubmitError",
        400: "documentCrafting.answersIncomplete",
        409: "documentCrafting.runStateChanged",
        ...RUN_START_SNACK,
      },
      {},
      // The questions step shows `errors[question_id]` on each question.
      ["invalid_answers"],
    ),
  );
  return response.data;
};

export const cancelDocumentCraftingRun = async (
  runId: number,
): Promise<DocumentCraftingRun> => {
  const response = await call.post<DocumentCraftingRun>(
    `${BASE}/runs/${runId}/cancel/`,
    {},
    options({
      success: "documentCrafting.runCancelSuccess",
      error: "documentCrafting.runCancelError",
      409: "documentCrafting.runStateChanged",
    }),
  );
  return response.data;
};

export const retryDocumentCraftingRun = async (
  runId: number,
): Promise<{ run_id: number }> => {
  const response = await call.post<{ run_id: number }>(
    `${BASE}/runs/${runId}/retry/`,
    {},
    options({
      error: "documentCrafting.runRetryError",
      409: "documentCrafting.runStateChanged",
      ...RUN_START_SNACK,
    }),
  );
  return response.data;
};
